import { supabase } from "@/integrations/supabase/client";

let refreshInFlight: Promise<boolean> | null = null;
let authRecoveryBarrier: Promise<unknown> | null = null;

const REQUEST_TIMEOUT_MS = 15_000;
const REFRESH_TIMEOUT_MS = 12_000;

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));

const withTimeout = async <T>(operation: PromiseLike<T>, milliseconds: number): Promise<T> => {
  let timeoutId: number | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error("Request timed out")), milliseconds);
  });

  try {
    return await Promise.race([Promise.resolve(operation), timeout]);
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  }
};

/**
 * Serializes foreground data requests behind the one session check performed
 * when a suspended tab becomes visible. Without this barrier, every mounted
 * page/hook can hit the API with the old token before refreshSession finishes.
 */
export const coordinateAuthRecovery = <T>(operation: () => Promise<T>): Promise<T> => {
  if (authRecoveryBarrier) return authRecoveryBarrier as Promise<T>;

  const recovery = operation();
  authRecoveryBarrier = recovery;
  const clearBarrier = () => {
    if (authRecoveryBarrier === recovery) authRecoveryBarrier = null;
  };
  void recovery.then(clearBarrier, clearBarrier);
  return recovery;
};

const waitForAuthRecovery = async () => {
  const barrier = authRecoveryBarrier;
  if (!barrier) return;

  try {
    await barrier;
  } catch {
    // The request below retains its normal retry/refresh behavior if the
    // coordinated foreground recovery could not refresh the session.
  }
};

const getErrorText = (error: unknown) => {
  if (!error || typeof error !== "object") return String(error ?? "");

  const candidate = error as { code?: unknown; message?: unknown; status?: unknown };
  return `${String(candidate.code ?? "")} ${String(candidate.status ?? "")} ${String(candidate.message ?? "")}`.toLowerCase();
};

export const isTransientAuthError = (error: unknown) => {
  const text = getErrorText(error);
  return (
    text.includes("pgrst301") ||
    text.includes("jwt expired") ||
    text.includes("invalid jwt") ||
    text.includes("401") ||
    text.includes("not authenticated") ||
    text.includes("auth session missing")
  );
};

const isTransientNetworkError = (error: unknown) => {
  const text = getErrorText(error);
  return (
    text.includes("failed to fetch") ||
    text.includes("networkerror") ||
    text.includes("network request failed") ||
    text.includes("load failed") ||
    text.includes("fetch failed") ||
    text.includes("request timed out")
  );
};

const waitForConnection = async (delay: number) => {
  if (navigator.onLine) {
    await wait(delay);
    return;
  }

  await Promise.race([
    new Promise<void>((resolve) => {
      window.addEventListener("online", () => resolve(), { once: true });
    }),
    wait(5000),
  ]);
};

const refreshAuthSession = async () => {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = withTimeout(supabase.auth.refreshSession(), REFRESH_TIMEOUT_MS)
    .then(({ data, error }) => !error && Boolean(data.session))
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};

/**
 * Retries one backend request after a coordinated token refresh. Browsers can
 * resume a suspended tab just before the auth client's automatic refresh has
 * replaced an expired access token, so the first request may legitimately
 * receive a transient 401 even though the user is still signed in.
 */
export const requestWithAuthRecovery = async <T extends { error: unknown }>(
  request: () => PromiseLike<T>,
): Promise<T> => {
  // PostgREST normally resolves with { data, error }, but when a browser tab
  // resumes before its network transport is ready, fetch itself rejects with
  // TypeError: Failed to fetch. Normalize that rejection so it enters the
  // same recovery loop instead of bypassing every retry in this helper.
  const runRequest = async (): Promise<T> => {
    try {
      await waitForAuthRecovery();
      return await withTimeout(request(), REQUEST_TIMEOUT_MS);
    } catch (error) {
      return { data: null, error } as unknown as T;
    }
  };

  let result = await runRequest();
  if (!result.error) return result;

  // A suspended browser tab can resume before its network connection is fully
  // usable. PostgREST reports that condition as TypeError: Failed to fetch,
  // not as an auth error, so retry it briefly instead of replacing real data
  // with an empty sheet and showing an immediate error.
  const retryDelays = [250, 750, 1500, 2500];
  const recoverNetwork = async () => {
    for (const delay of retryDelays) {
      if (!isTransientNetworkError(result.error)) return;
      await waitForConnection(delay);
      result = await runRequest();
      if (!result.error) return;
    }
  };

  await recoverNetwork();
  if (!result.error) return result;

  // Network recovery can reveal that the token expired while the tab was
  // suspended. Re-evaluate the latest error after reconnecting, then refresh
  // once and give the retried request the same transport recovery window.
  if (isTransientAuthError(result.error)) {
    const refreshed = await refreshAuthSession();
    if (refreshed) {
      result = await runRequest();
      if (!result.error) return result;
      await recoverNetwork();
    }
  }

  return result;
};